/**
 * Upload Step
 *
 * First step of the CSV import wizard.
 * Handles file upload with dropzone, object type selection, and CSV header parsing.
 */

import { AlertCircle, File02, Trash01 } from '@untitledui/icons';
import Papa, { type ParseResult } from 'papaparse';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react-aria-components';
import { useListData } from 'react-stately';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { FileDropZone } from '@/lib/base/file-upload';
import { Input } from '@/lib/base/forms/input/input';
import {
  MultiSelect,
  type RenderTagFn,
} from '@/lib/base/forms/select/multi-select';
import type { SelectItemType } from '@/lib/base/forms/select/select';
import { TagCloseX } from '@/lib/base/tags/tag-close-x';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons';
import type {
  MultiTypeMappingSuggestion,
  NewPropertySuggestion,
  UploadedFileEntry,
} from '@/lib/csv-import/api';
import {
  suggestMappings,
  suggestMultiTypeMappings,
} from '@/lib/csv-import/api';
import { fileService } from '@/lib/files/service';
import { logger } from '@/lib/logger';

import { synthesizeSingleTypeSuggestion } from '../csv-import-helpers';
import { useImportableEntityTypes } from '../hooks/useImportTargetTypes';
import type { UseCsvImportReturn } from '../useCsvImport';
import { DataCleanupPrompt } from './DataCleanupPrompt';

const CSV_ACCEPTED_TYPES =
  '.csv,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Max sample rows to extract for the entity preview card. */
const SAMPLE_ROW_COUNT = 5;

async function parseXlsxHeaders(file: File): Promise<{
  headers: string[];
  rowCount: number;
  sampleRows: Record<string, string>[];
  /** CSV-converted blob for streaming search (PapaParse can't read XLSX). */
  csvBlob: Blob;
}> {
  try {
    const buffer = await file.arrayBuffer();
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const sheet = workbook.worksheets[0];
    if (!sheet || sheet.actualRowCount === 0) {
      return {
        headers: [],
        rowCount: 0,
        sampleRows: [],
        csvBlob: new Blob([], { type: 'text/csv' }),
      };
    }
    // XLSX files can have leading blank rows before the header row. Scan from
    // the top for the first row with a non-empty cell and treat that as the
    // header row — this mirrors the old SheetJS `blankrows: false` behavior.
    // Using physical row 1 unconditionally would yield empty headers (and bad
    // mapping suggestions) for files that start with blank rows.
    let headerRowNumber = 0;
    for (let rowNumber = 1; rowNumber <= sheet.rowCount; rowNumber++) {
      let rowHasValue = false;
      sheet.getRow(rowNumber).eachCell({ includeEmpty: false }, cell => {
        if (String(cell.value ?? '').trim() !== '') {
          rowHasValue = true;
        }
      });
      if (rowHasValue) {
        headerRowNumber = rowNumber;
        break;
      }
    }
    if (headerRowNumber === 0) {
      return {
        headers: [],
        rowCount: 0,
        sampleRows: [],
        csvBlob: new Blob([], { type: 'text/csv' }),
      };
    }
    const headerRow = sheet.getRow(headerRowNumber);
    const headers: string[] = [];
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      headers[colNumber - 1] = String(cell.value ?? '');
    });
    while (headers.length > 0 && headers[headers.length - 1] === '') {
      headers.pop();
    }
    // actualRowCount counts only rows that contain values (leading/interspersed
    // blank rows are excluded), so subtracting 1 for the header row gives the
    // data row count regardless of where the header row physically sits.
    const rowCount = Math.max(0, sheet.actualRowCount - 1);

    // Extract the first few data rows for the entity preview card.
    const sampleRows: Record<string, string>[] = [];
    for (
      let rowNumber = headerRowNumber + 1;
      rowNumber <= sheet.rowCount && sampleRows.length < SAMPLE_ROW_COUNT;
      rowNumber++
    ) {
      const row = sheet.getRow(rowNumber);
      // Skip blank rows — they don't contribute meaningful sample data.
      let hasValue = false;
      const obj: Record<string, string> = {};
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const idx = colNumber - 1;
        if (idx < headers.length) {
          const val = String(cell.value ?? '');
          obj[headers[idx]] = val;
          if (val.trim()) {
            hasValue = true;
          }
        }
      });
      if (hasValue) {
        // Fill in missing headers with empty strings so every row has every key.
        for (const h of headers) {
          if (!(h in obj)) {
            obj[h] = '';
          }
        }
        sampleRows.push(obj);
      }
    }

    // Convert the entire sheet to CSV so PapaParse can stream-search it later.
    // Papa.unparse expects an array of arrays: [headers, ...dataRows].
    const allRows: string[][] = [headers];
    for (
      let rowNumber = headerRowNumber + 1;
      rowNumber <= sheet.rowCount;
      rowNumber++
    ) {
      const row = sheet.getRow(rowNumber);
      const cells: string[] = new Array(headers.length).fill('');
      let rowHasData = false;
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const idx = colNumber - 1;
        if (idx < headers.length) {
          const val = String(cell.value ?? '');
          cells[idx] = val;
          if (val.trim()) {
            rowHasData = true;
          }
        }
      });
      if (rowHasData) {
        allRows.push(cells);
      }
    }
    const csvString = Papa.unparse(allRows);
    const csvBlob = new Blob([csvString], { type: 'text/csv' });

    return { headers, rowCount, sampleRows, csvBlob };
  } catch (error) {
    logger.warn('[CSV] parseXlsxHeaders failed', {
      component: 'UploadStep',
      error_type: error instanceof Error ? error.constructor.name : 'unknown',
      original_message: error instanceof Error ? error.message : String(error),
    });
    throw new Error(
      'Unable to read Excel file. The file may be corrupted or in an unsupported format.'
    );
  }
}

// ============================================================================
// Types
// ============================================================================

interface UploadStepProps {
  orgId: number;
  state: UseCsvImportReturn;
}

interface FileWithPreview {
  file_id: number;
  tempId?: string; // Unique ID for in-flight uploads before file_id is assigned
  filename: string;
  // File size in bytes. Used for filename+size duplicate detection. Restored
  // entries (loaded from parent state on Back-nav) won't have this — dedup
  // falls back to filename-only matching for them.
  size?: number;
  entity_type_names: string[];
  headers: string[];
  rowCount: number;
  isUploading?: boolean;
  uploadProgress?: number;
  error?: string;
  // AbortController for the in-flight upload — attached only while
  // isUploading. handleRemove uses this to cancel a running upload when the
  // user clicks the trash icon mid-progress.
  abortController?: AbortController;
  multiTypeSuggestion?: MultiTypeMappingSuggestion | null;
  multiTypeSuggestionError?: string | null;
}

const renderEntityTypeItem = (item: SelectItemType) => (
  <MultiSelect.Item id={item.id} label={item.label} icon={item.icon} />
);

/** Prevent pointer-down on the close button from propagating up to the
 *  combobox trigger, which would open the dropdown via menuTrigger='focus'. */
const stopPointerDown = (e: React.PointerEvent) => {
  e.stopPropagation();
};

const renderEntityTypeTag: RenderTagFn = (item, onRemove) => {
  const Icon = getEntityTypeIconComponent(item.id);
  return (
    <span
      key={item.id}
      className='flex items-center gap-1 rounded-md bg-primary py-0.5 pl-1.5 pr-1 ring-1 ring-primary ring-inset'
    >
      <Icon className='size-3.5 shrink-0 text-fg-quaternary' />
      <span className='truncate text-sm font-medium text-secondary select-none'>
        {item.label}
      </span>
      <span onPointerDown={stopPointerDown}>
        <TagCloseX
          size='md'
          className='ml-0.5'
          onPress={() => onRemove(item.id)}
        />
      </span>
    </span>
  );
};

// ============================================================================
// Entity Type Select
// ============================================================================

function EntityTypeSelect({
  selectedTypes,
  onChange,
  options,
}: {
  selectedTypes: string[];
  onChange: (types: string[]) => void;
  options: SelectItemType[];
}) {
  const initialSelectedItems = useMemo<SelectItemType[]>(
    () =>
      selectedTypes
        .map(id => options.find(o => o.id === id))
        .filter((item): item is SelectItemType => item !== undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only compute initial items once
    []
  );

  const selectedItems = useListData<SelectItemType>({
    initialItems: initialSelectedItems,
  });

  // Sync external selectedTypes → internal useListData when the parent changes
  // (e.g. multi-type auto-detection completing after mount, or Back-nav).
  // Also re-syncs when `options` changes — entity types load async, and without
  // this the initial render computes empty `desiredItems` and selected tags
  // would render blank until selectedTypes itself changes.
  // Safe now that MultiSelect's internal sync effect uses a ref-based guard
  // that prevents the cascade that previously caused an infinite loop.
  useEffect(() => {
    const currentIds = selectedItems.items.map(i => String(i.id));
    if (JSON.stringify(currentIds) === JSON.stringify(selectedTypes)) {
      return;
    }
    const desiredItems = selectedTypes
      .map(id => options.find(o => o.id === id))
      .filter((item): item is SelectItemType => item !== undefined);
    if (currentIds.length > 0) {
      selectedItems.remove(...currentIds);
    }
    if (desiredItems.length > 0) {
      selectedItems.append(...desiredItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedItems is a new ref each render; sync only when external props change
  }, [selectedTypes, options]);

  const handleItemInserted = useCallback(
    (key: Key) => {
      onChange([...selectedTypes, String(key)]);
    },
    [selectedTypes, onChange]
  );

  const handleItemCleared = useCallback(
    (key: Key) => {
      // Don't allow deselecting the last type
      if (selectedTypes.length <= 1) {
        return;
      }
      onChange(selectedTypes.filter(t => t !== String(key)));
    },
    [selectedTypes, onChange]
  );

  return (
    <MultiSelect
      label='Import as'
      hint='Each row will create or update a record of this type'
      placeholder='Select types'
      shortcut={false}
      items={options}
      selectedItems={selectedItems}
      onItemInserted={handleItemInserted}
      onItemCleared={handleItemCleared}
      renderTag={renderEntityTypeTag}
      size='sm'
    >
      {renderEntityTypeItem}
    </MultiSelect>
  );
}

// ============================================================================
// File Item Component
// ============================================================================

interface FileItemProps {
  file: FileWithPreview;
  onRemove: () => void;
  onEntityTypeChange: (entityTypeNames: string[]) => void;
  entityTypeOptions: SelectItemType[];
}

const COLUMN_COLLAPSED_LIMIT = 8;

function FileItem({
  file,
  onRemove,
  onEntityTypeChange,
  entityTypeOptions,
}: FileItemProps) {
  let uploadPhase: 'uploading' | 'analyzing' | null = null;
  if (file.isUploading) {
    uploadPhase = (file.uploadProgress || 0) >= 70 ? 'analyzing' : 'uploading';
  }

  const [columnsExpanded, setColumnsExpanded] = useState(false);
  const handleToggleColumns = useCallback(
    () => setColumnsExpanded(prev => !prev),
    []
  );

  const visibleHeaders = columnsExpanded
    ? file.headers
    : file.headers.slice(0, COLUMN_COLLAPSED_LIMIT);
  const hiddenColumnCount = file.headers.length - COLUMN_COLLAPSED_LIMIT;

  const visibleHeadersWithKeys = useMemo(() => {
    const seen = new Map<string, number>();
    return visibleHeaders.map(header => {
      const count = seen.get(header) ?? 0;
      seen.set(header, count + 1);
      return { header, key: `${header}::${count}` };
    });
  }, [visibleHeaders]);

  return (
    <div className='rounded-lg border border-border-secondary bg-bg-primary p-4'>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex items-start gap-3 min-w-0 flex-1'>
          <div className='flex-shrink-0 rounded-lg bg-bg-tertiary p-2'>
            <File02 className='h-5 w-5 text-fg-tertiary' />
          </div>
          <div className='min-w-0 flex-1'>
            <div className='truncate text-sm font-medium text-fg-primary'>
              {file.filename}
            </div>
            <div className='mt-1 text-xs text-fg-quaternary'>
              {file.rowCount} {file.rowCount === 1 ? 'row' : 'rows'} •{' '}
              {file.headers.length}{' '}
              {file.headers.length === 1 ? 'column' : 'columns'}
            </div>
            {file.error && (
              <div className='mt-1 flex items-center gap-1 text-xs text-error-600'>
                <AlertCircle className='h-3 w-3' />
                {file.error}
              </div>
            )}

            {/* Entity type selector — shown after upload completes */}
            {!file.isUploading && (
              <div className='mt-2'>
                <EntityTypeSelect
                  selectedTypes={file.entity_type_names}
                  onChange={onEntityTypeChange}
                  options={entityTypeOptions}
                />
              </div>
            )}
          </div>
        </div>

        <Button
          color='tertiary'
          size='sm'
          iconLeading={Trash01}
          onClick={onRemove}
          title={file.isUploading ? 'Cancel upload' : 'Remove file'}
          aria-label={
            file.isUploading
              ? `Cancel upload of ${file.filename}`
              : `Remove ${file.filename}`
          }
        />
      </div>

      {/* Upload progress */}
      {file.isUploading && (
        <div className='mt-3'>
          <div
            className='mb-1'
            aria-live='polite'
            aria-busy='true'
            aria-atomic='true'
          >
            <span className='text-xs text-fg-quaternary'>
              {uploadPhase === 'analyzing'
                ? 'Analyzing columns…'
                : 'Uploading…'}
            </span>
          </div>
          <div className='h-1.5 w-full overflow-hidden rounded-full bg-bg-tertiary'>
            <div
              className='h-full rounded-full bg-brand-500 transition-all duration-300 ease-out'
              style={{ width: `${file.uploadProgress || 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Headers preview */}
      {!file.isUploading && file.headers.length > 0 && (
        <div className='mt-3'>
          <div className='text-xs font-medium text-fg-quaternary mb-2'>
            Columns:
          </div>
          <div className='flex flex-wrap gap-1.5'>
            {visibleHeadersWithKeys.map(({ header, key }) => (
              <span
                key={key}
                className='inline-flex items-center rounded-md bg-bg-tertiary px-2 py-0.5 text-xs font-medium text-fg-secondary'
              >
                {header}
              </span>
            ))}
            {hiddenColumnCount > 0 && (
              <Button
                color='tertiary'
                size='sm'
                onClick={handleToggleColumns}
                aria-expanded={columnsExpanded}
                aria-label={
                  columnsExpanded
                    ? 'Show fewer columns'
                    : `Show all ${file.headers.length} columns`
                }
                className='inline-flex items-center rounded-md bg-bg-tertiary px-2 py-0.5 text-xs font-medium text-fg-quaternary hover:bg-bg-secondary hover:text-fg-secondary'
              >
                {columnsExpanded ? 'Show less' : `+${hiddenColumnCount} more`}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FileItemWrapper
// ============================================================================

interface FileItemWrapperProps {
  file: FileWithPreview;
  onRemoveFile: (file: FileWithPreview) => void;
  onEntityTypeChange: (fileId: number, types: string[]) => void;
  entityTypeOptions: SelectItemType[];
}

const FileItemWrapper = memo(function FileItemWrapper({
  file,
  onRemoveFile,
  onEntityTypeChange,
  entityTypeOptions,
}: FileItemWrapperProps) {
  const handleRemove = useCallback(
    () => onRemoveFile(file),
    [onRemoveFile, file]
  );
  const handleEntityTypeChange = useCallback(
    (types: string[]) => onEntityTypeChange(file.file_id, types),
    [onEntityTypeChange, file.file_id]
  );
  return (
    <FileItem
      file={file}
      onRemove={handleRemove}
      onEntityTypeChange={handleEntityTypeChange}
      entityTypeOptions={entityTypeOptions}
    />
  );
});

// ============================================================================
// Main Component
// ============================================================================

export function UploadStep({ orgId, state }: UploadStepProps) {
  // Destructure ALL setters/values up-front. The `state` object itself is a
  // fresh reference on every render (returned by useCsvImport), so depending
  // on `state` in a useCallback re-creates the callback every render and
  // defeats `memo` on FileItemWrapper. Reading the individual setters once
  // gives us stable refs to depend on.
  const {
    importName,
    setImportName,
    uploadedFiles,
    setUploadedFiles,
    setColumnMappings,
    setSuggestedAssociations,
    setFileColumns,
    setFileRowCounts,
    setFileSampleRows,
    setFileObjects,
    handleMultiTypeSuggestionAccepted,
    seedMatchColumns,
    setUploadingFileCount,
    clearMultiTypeModeForFile,
    setDirtyTypes,
    multiTypeSuggestions,
  } = state;
  const [autoActivateMessage, setAutoActivateMessage] = useState<string>('');
  const [importNameTouched, setImportNameTouched] = useState(false);

  // Schema-driven entity types a file can be imported into: core system record
  // types + the org's custom domain types (org_workspace, etc.).
  const importableTypes = useImportableEntityTypes(orgId);
  const entityTypeOptions = useMemo<SelectItemType[]>(
    () =>
      importableTypes.map(t => ({
        id: t.value,
        label: t.label,
        icon: getEntityTypeIconComponent(t.value),
      })),
    [importableTypes]
  );

  // Initialize from persisted hook state so "Back" restores the file list
  const [localFiles, setLocalFiles] = useState<FileWithPreview[]>(() =>
    state.uploadedFiles.map(uf => {
      const fileId =
        typeof uf.file_id === 'number' ? uf.file_id : Number(uf.file_id);
      const perFileSuggestion = state.multiTypeSuggestions[fileId] ?? null;
      return {
        file_id: fileId,
        filename: uf.filename,
        entity_type_names: perFileSuggestion
          ? perFileSuggestion.entity_types.map(et => et.entity_type_name)
          : [uf.entity_type_name],
        headers: state.fileColumns[fileId] ?? [],
        rowCount: state.fileRowCounts[fileId] ?? 0,
        isUploading: false,
        multiTypeSuggestion: perFileSuggestion,
      };
    })
  );
  const abortControllersRef = useRef<AbortController[]>([]);
  // Tracks all pending requestAnimationFrame IDs so unmount cleanup can cancel them.
  const animationFrameIdsRef = useRef<Set<number>>(new Set());
  const autoActivateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // Mirror localFiles in a ref so callbacks can read it without listing it in
  // their deps. Listing localFiles in handleEntityTypeChange would change its
  // reference on every upload-progress tick, defeating memo on FileItemWrapper.
  const localFilesRef = useRef(localFiles);
  useEffect(() => {
    localFilesRef.current = localFiles;
  }, [localFiles]);

  // Parse file headers, row count, and (for xlsx) sample rows. Branches on
  // file extension: xlsx files use ExcelJS (lazy-loaded); csv files use
  // PapaParse streaming (sample rows are parsed separately for CSV later).
  const parseFileHeaders = useCallback(
    (
      file: File
    ): Promise<{
      headers: string[];
      rowCount: number;
      sampleRows?: Record<string, string>[];
      csvBlob?: Blob;
    }> => {
      logger.debug('[Upload] parseFileHeaders called', {
        fileName: file.name,
        size: file.size,
      });
      if (file.name.toLowerCase().endsWith('.xlsx')) {
        return parseXlsxHeaders(file);
      }
      return new Promise((resolve, reject) => {
        logger.debug('[Upload] Starting Papa.parse...');
        let headers: string[] = [];
        let totalRows = 0;
        Papa.parse(file, {
          // Run parsing in a Web Worker so counting rows in large CSV files
          // doesn't block the main thread / freeze the UI.
          worker: true,
          skipEmptyLines: true,
          step: (results: { data: unknown }) => {
            if (totalRows === 0) {
              headers = (results.data as string[]) || [];
            }
            totalRows++;
          },
          complete: () => {
            const rowCount = Math.max(0, totalRows - 1);
            logger.debug('[Upload] Parsed headers', { headers, rowCount });
            resolve({ headers, rowCount });
          },
          error: (error: Error) => {
            logger.error('[Upload] Parse error callback fired', error, {
              component: 'UploadStep',
            });
            reject(error);
          },
        });
        logger.debug('[Upload] Papa.parse called (sync part done)');
      });
    },
    []
  );

  // Handle file upload
  const uploadFile = useCallback(
    async (file: File) => {
      logger.debug('[CSV] uploadFile called', { fileName: file.name });
      const tempId = `temp-${Date.now()}-${Math.random()}`;

      // Create AbortController for this upload
      const abortController = new AbortController();
      abortControllersRef.current.push(abortController);

      // Create temporary entry with uploading state
      // Use tempId for unique identification of in-flight uploads
      const tempEntry: FileWithPreview = {
        file_id: 0,
        tempId, // Use tempId to uniquely identify this upload
        filename: file.name,
        size: file.size, // Used for dedup checks against subsequent picks
        entity_type_names: ['ahoy_contact'], // Default
        headers: [],
        rowCount: 0,
        isUploading: true,
        uploadProgress: 0,
        abortController, // Lets handleRemove cancel an in-flight upload
      };

      logger.debug('[CSV] Setting temp entry in localFiles', { tempId });
      setLocalFiles(prev => [...prev, tempEntry]);
      // Block the wizard's "Next" CTA until this upload settles. Paired with
      // the matching decrement in the `finally` block below so success, error,
      // and unmount-driven abort all release the lock.
      setUploadingFileCount(prev => prev + 1);

      try {
        // Parse headers (and sample rows for xlsx, which are extracted in the
        // same pass over the workbook).
        logger.debug('[Upload] About to call parseFileHeaders...');
        const {
          headers,
          rowCount,
          sampleRows: xlsxSampleRows,
          csvBlob: xlsxCsvBlob,
        } = await parseFileHeaders(file);
        logger.debug('[Upload] parseFileHeaders returned', {
          headerCount: headers.length,
          rowCount,
        });

        // Update with headers while still uploading - match by tempId
        setLocalFiles(prev =>
          prev.map(f => (f.tempId === tempId ? { ...f, headers, rowCount } : f))
        );

        // Upload file to server (progress 0-70%)
        logger.debug('[CSV] About to call fileService.uploadFile...');
        const uploadedFile = await fileService.uploadFile(orgId, file, {
          onProgress: progress => {
            // Scale upload progress to 0-70%
            const scaledProgress = Math.round(progress * 0.7);
            logger.debug('[CSV] Upload progress', { progress, scaledProgress });
            setLocalFiles(prev =>
              prev.map(f =>
                f.tempId === tempId
                  ? { ...f, uploadProgress: scaledProgress }
                  : f
              )
            );
          },
        });
        logger.debug('[CSV] fileService.uploadFile returned', { uploadedFile });

        // Start smooth animated progress for AI analysis phase (70-95%)
        // Uses requestAnimationFrame with ease-out cubic for buttery-smooth 60fps animation
        // Pattern from GmailConnectStep.tsx
        const startProgress = 70;
        const targetProgress = 95;
        const duration = 4000; // 4 seconds
        const startTime = Date.now();
        let animationFrameId: number | null = null;

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const rawProgress = Math.min(elapsed / duration, 1);

          // Ease-out cubic: fast start, slows down as it approaches target
          const easedProgress = 1 - Math.pow(1 - rawProgress, 3);
          const currentProgress =
            startProgress + easedProgress * (targetProgress - startProgress);

          setLocalFiles(prev =>
            prev.map(f =>
              f.tempId === tempId
                ? { ...f, uploadProgress: Math.round(currentProgress) }
                : f
            )
          );

          if (rawProgress < 1) {
            if (animationFrameId !== null) {
              animationFrameIdsRef.current.delete(animationFrameId);
            }
            animationFrameId = requestAnimationFrame(animate);
            animationFrameIdsRef.current.add(animationFrameId);
          } else {
            if (animationFrameId !== null) {
              animationFrameIdsRef.current.delete(animationFrameId);
              animationFrameId = null;
            }
          }
        };

        animationFrameId = requestAnimationFrame(animate);
        animationFrameIdsRef.current.add(animationFrameId);

        // Parse sample rows early — both suggestMappings (for field-type
        // inference on new properties) and suggestMultiTypeMappings need them.
        const parseSampleRows = (): Promise<Record<string, string>[]> => {
          return new Promise((resolve, reject) => {
            Papa.parse(file, {
              preview: 6, // Header + 5 sample rows
              worker: false,
              complete: (results: ParseResult<unknown>) => {
                const data = results.data as string[][];
                const headerRow = data[0] || [];
                const dataRows = data.slice(1, 6);

                // Convert array rows to objects keyed by column names
                const rows = dataRows.map(row => {
                  const obj: Record<string, string> = {};
                  headerRow.forEach((header, idx) => {
                    obj[header] = row[idx] || '';
                  });
                  return obj;
                });

                resolve(rows);
              },
              error: (error: Error) => {
                reject(error);
              },
            });
          });
        };

        let sampleData: Record<string, string>[] = [];
        const isXlsx = file.name.toLowerCase().endsWith('.xlsx');
        if (isXlsx) {
          // XLSX sample rows were already extracted during header parsing.
          sampleData = xlsxSampleRows ?? [];
        } else {
          try {
            sampleData = await parseSampleRows();
          } catch (error) {
            logger.warn('[CSV] Failed to parse sample rows', {
              error,
              component: 'UploadStep',
            });
          }
        }
        if (sampleData.length > 0) {
          setFileSampleRows(prev => ({
            ...prev,
            [String(uploadedFile.id)]: sampleData,
          }));
        }

        // Store the file object (or XLSX-to-CSV blob) for streaming search.
        const searchableFile = isXlsx ? xlsxCsvBlob : file;
        if (searchableFile) {
          setFileObjects(prev => ({
            ...prev,
            [String(uploadedFile.id)]: searchableFile,
          }));
        }

        // Call AI for suggestions (progress 70-100%)
        let suggestedEntityTypeName = 'ahoy_contact';
        let suggestedMappings: Record<string, string> = {};
        let suggestedNewProperties: NewPropertySuggestion[] = [];

        try {
          logger.debug('[CSV] Calling suggestMappings', {
            filename: file.name,
            headers,
          });
          const suggestions = await suggestMappings(
            orgId,
            headers,
            undefined,
            sampleData.length > 0 ? sampleData : undefined,
            abortController.signal
          );
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameIdsRef.current.delete(animationFrameId);
            animationFrameId = null;
          }
          logger.debug('[CSV] suggestMappings returned', {
            filename: file.name,
            entity_type_name: suggestions.entity_type_name,
            mappings: suggestions.mappings,
            new_properties: suggestions.new_properties?.length ?? 0,
          });
          suggestedEntityTypeName = suggestions.entity_type_name;
          suggestedMappings = suggestions.mappings;
          suggestedNewProperties = suggestions.new_properties ?? [];

          // Store mappings for MappingStep via virtual key so all state uses editKeys
          handleMultiTypeSuggestionAccepted(
            uploadedFile.id,
            synthesizeSingleTypeSuggestion(
              suggestedEntityTypeName,
              suggestedMappings,
              suggestedNewProperties,
              suggestions.internal_columns
            )
          );

          // Pre-select suggested ID-like columns as match columns on the
          // default upload path (mirrors the dirty-type re-suggestion path).
          seedMatchColumns(
            `${uploadedFile.id}_virtual_${suggestedEntityTypeName}`,
            suggestions.suggested_match_columns ?? []
          );

          // Store suggested associations for MappingStep to auto-populate
          if (suggestions.associations && suggestions.associations.length > 0) {
            logger.debug('[CSV] Storing suggested associations', {
              associations: suggestions.associations,
            });
            setSuggestedAssociations(prev => ({
              ...prev,
              [String(uploadedFile.id)]: suggestions.associations,
            }));
          }
        } catch (error) {
          // Haiku failed - continue with defaults
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameIdsRef.current.delete(animationFrameId);
            animationFrameId = null;
          }
          logger.warn('[CSV] AI mapping suggestions failed', {
            error,
            component: 'UploadStep',
          });
        }

        // Call multi-type detection (non-blocking)
        let multiTypeSuggestion: MultiTypeMappingSuggestion | null = null;
        let multiTypeSuggestionError: string | null = null;

        try {
          logger.debug('[CSV] Calling suggestMultiTypeMappings', {
            filename: file.name,
            headers,
          });

          // Wrap in 30-second timeout — always clear the timer after the race
          // to prevent the setTimeout from keeping the event loop alive.
          let multiTypeTimeoutId: ReturnType<typeof setTimeout> | null = null;
          const timeoutPromise = new Promise<MultiTypeMappingSuggestion>(
            (_resolve, reject) => {
              multiTypeTimeoutId = setTimeout(
                () => reject(new Error('Timeout')),
                30000
              );
            }
          );

          const detectionPromise = suggestMultiTypeMappings(
            orgId,
            headers,
            sampleData,
            abortController.signal
          );

          try {
            multiTypeSuggestion = await Promise.race([
              detectionPromise,
              timeoutPromise,
            ]);
          } finally {
            if (multiTypeTimeoutId !== null) {
              clearTimeout(multiTypeTimeoutId);
            }
          }

          logger.debug('[CSV] suggestMultiTypeMappings returned', {
            filename: file.name,
            is_multi_type: multiTypeSuggestion.is_multi_type,
            entity_types: multiTypeSuggestion.entity_types,
            confidence: multiTypeSuggestion.confidence,
          });
        } catch (error) {
          // Multi-type detection failed or timed out - continue with single-type flow
          logger.warn('[CSV] Multi-type detection failed', {
            error,
            component: 'UploadStep',
          });
          multiTypeSuggestionError =
            error instanceof Error ? error.message : 'Detection failed';
        }

        if (abortController.signal.aborted) {
          return;
        }

        // Update progress to 100%
        setLocalFiles(prev =>
          prev.map(f =>
            f.tempId === tempId ? { ...f, uploadProgress: 100 } : f
          )
        );

        // Per-file auto-activation: each file independently activates multi-type
        const shouldAutoActivate =
          (multiTypeSuggestion?.is_multi_type ?? false) &&
          (multiTypeSuggestion?.confidence ?? 0) >= 0.7;

        const entityTypeNames =
          shouldAutoActivate && multiTypeSuggestion
            ? multiTypeSuggestion.entity_types.map(et => et.entity_type_name)
            : [suggestedEntityTypeName];

        const finalEntry: FileWithPreview = {
          file_id: uploadedFile.id,
          filename: uploadedFile.original_filename,
          size: file.size, // Preserve for subsequent duplicate-detection checks
          entity_type_names: entityTypeNames,
          headers,
          rowCount,
          isUploading: false,
          multiTypeSuggestion,
          multiTypeSuggestionError,
        };

        setLocalFiles(prev =>
          prev.map(f => (f.tempId === tempId ? finalEntry : f))
        );

        if (shouldAutoActivate && multiTypeSuggestion) {
          handleMultiTypeSuggestionAccepted(
            uploadedFile.id,
            multiTypeSuggestion
          );
          // Pre-select suggested ID-like columns as match columns for every
          // auto-activated entity type. suggested_match_columns is one flat
          // list for the whole file, applied identically to each type.
          for (const et of multiTypeSuggestion.entity_types) {
            seedMatchColumns(
              `${uploadedFile.id}_virtual_${et.entity_type_name}`,
              multiTypeSuggestion.suggested_match_columns ?? []
            );
          }
          setAutoActivateMessage(
            'Multi-type import mode activated based on detected object types'
          );
          if (autoActivateTimerRef.current !== null) {
            clearTimeout(autoActivateTimerRef.current);
          }
          autoActivateTimerRef.current = setTimeout(() => {
            setAutoActivateMessage('');
            autoActivateTimerRef.current = null;
          }, 1000);
        }

        // Update parent state using functional update to avoid stale state
        setUploadedFiles(prev => {
          const newUploadedFile: UploadedFileEntry = {
            file_id: uploadedFile.id,
            filename: uploadedFile.original_filename,
            entity_type_name: entityTypeNames[0],
            ...(suggestedNewProperties.length > 0
              ? { new_properties: suggestedNewProperties }
              : {}),
          };
          return [...prev, newUploadedFile];
        });
        setFileColumns(prev => ({
          ...prev,
          [uploadedFile.id]: headers,
        }));
        setFileRowCounts(prev => ({
          ...prev,
          [uploadedFile.id]: rowCount,
        }));
      } catch (error) {
        // User-initiated cancel (via the trash icon) and unmount cleanup both
        // route through abortController.abort(), which surfaces as an
        // AbortError. The entry has already been (or will be) removed by
        // handleRemove — don't log it as a failure or paint the row red.
        if (abortController.signal.aborted) {
          logger.debug('[CSV] uploadFile aborted', { tempId });
        } else {
          logger.error(
            '[CSV] uploadFile error',
            error instanceof Error ? error : undefined,
            { component: 'UploadStep' }
          );
          const message = "We couldn't upload this file. Try again.";
          setLocalFiles(prev =>
            prev.map(f =>
              f.tempId === tempId
                ? { ...f, isUploading: false, error: message }
                : f
            )
          );
        }
      } finally {
        // Remove this controller from the ref
        abortControllersRef.current = abortControllersRef.current.filter(
          controller => controller !== abortController
        );
        // Release the wizard-level upload lock. Functional update keeps the
        // counter floored at zero if the parent has already been reset.
        setUploadingFileCount(prev => Math.max(0, prev - 1));
      }
    },
    [
      orgId,
      setUploadedFiles,
      parseFileHeaders,
      setSuggestedAssociations,
      setFileColumns,
      setFileRowCounts,
      setFileSampleRows,
      setFileObjects,
      handleMultiTypeSuggestionAccepted,
      seedMatchColumns,
      setUploadingFileCount,
    ]
  );

  // Handle files from drop or input
  const handleFiles = useCallback(
    (files: FileList) => {
      logger.debug('[CSV] handleFiles called', { fileCount: files.length });

      const acceptedFiles = Array.from(files).filter(
        file =>
          file.type === 'text/csv' ||
          file.name.toLowerCase().endsWith('.csv') ||
          file.type === 'application/vnd.ms-excel' ||
          file.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.name.toLowerCase().endsWith('.xlsx')
      );

      logger.debug('[Upload] Filtered accepted files', {
        count: acceptedFiles.length,
        fileNames: acceptedFiles.map(f => f.name),
      });
      if (acceptedFiles.length === 0) {
        logger.debug('[Upload] No accepted files found, returning');
        return;
      }

      // Dedup against (a) files currently in the upload list (in-progress or
      // completed-without-error) and (b) earlier files in this same batch.
      // Match on filename + size. Restored entries from parent state (Back-nav)
      // won't have a `size` recorded — for those, filename alone is enough.
      // Errored entries remain retry-eligible so the user can re-pick after a
      // transient failure.
      const seenInBatch = new Set<string>();
      const accepted: File[] = [];
      const skippedNames: string[] = [];

      for (const file of acceptedFiles) {
        const batchKey = `${file.name}|${file.size}`;
        if (seenInBatch.has(batchKey)) {
          skippedNames.push(file.name);
          continue;
        }
        const isDuplicate = localFiles.some(local => {
          if (local.error) {
            return false;
          }
          if (local.filename !== file.name) {
            return false;
          }
          return local.size === undefined || local.size === file.size;
        });
        if (isDuplicate) {
          skippedNames.push(file.name);
          continue;
        }
        seenInBatch.add(batchKey);
        accepted.push(file);
      }

      if (skippedNames.length > 0) {
        const first = skippedNames[0];
        const extra = skippedNames.length - 1;
        let message: string;
        if (extra === 0) {
          message = `Skipped "${first}" – already uploaded`;
        } else {
          const pluralSuffix = extra > 1 ? 's' : '';
          message = `Skipped "${first}" and ${extra} other already-uploaded file${pluralSuffix}`;
        }
        toast.message(message);
        logger.info('[CSV] Skipped duplicate uploads', {
          skipped: skippedNames,
        });
      }

      if (accepted.length === 0) {
        return;
      }

      // Auto-populate import name from first accepted file if name is empty
      if (importName.trim() === '') {
        const firstName = accepted[0].name.replace(/\.(csv|xlsx)$/i, '');
        setImportName(firstName);
      }

      accepted.forEach(file => {
        logger.debug('[CSV] Calling uploadFile', { fileName: file.name });
        uploadFile(file);
      });
    },
    [uploadFile, importName, setImportName, localFiles]
  );

  const handleImportNameBlur = useCallback(() => {
    setImportNameTouched(true);
  }, []);

  // Remove file (or cancel an in-flight upload). Takes the whole entry so we
  // can identify in-progress uploads (which have file_id === 0) by tempId, and
  // so we can reach the AbortController without a side-channel map.
  const handleRemove = useCallback(
    (file: FileWithPreview) => {
      // In-flight upload: abort the request, drop the entry. uploadFile's
      // finally-block will decrement uploadingFileCount and clean up the
      // global controllers ref. The catch path's setLocalFiles is a no-op
      // because we've already filtered the entry out.
      if (file.isUploading) {
        file.abortController?.abort();
        if (file.tempId) {
          setLocalFiles(prev => prev.filter(f => f.tempId !== file.tempId));
        }
        return;
      }

      // Completed or errored entry: existing removal path.
      const fileId = file.file_id;
      setLocalFiles(prev => prev.filter(f => f.file_id !== fileId));
      setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
      setFileColumns(prev => {
        const { [fileId]: _removed, ...rest } = prev;
        return rest;
      });
      setFileRowCounts(prev => {
        const { [fileId]: _removed, ...rest } = prev;
        return rest;
      });
      // Clean up per-file multi-type state if this file had it
      clearMultiTypeModeForFile(fileId);
    },
    [
      setUploadedFiles,
      setFileColumns,
      setFileRowCounts,
      clearMultiTypeModeForFile,
    ]
  );

  // Update entity types (supports multi-select)
  const handleEntityTypeChange = useCallback(
    (fileId: number, entityTypeNames: string[]) => {
      if (entityTypeNames.length === 0) {
        return;
      }

      const primaryType = entityTypeNames[0];
      const fileIdStr = String(fileId);

      setLocalFiles(prev =>
        prev.map(f =>
          f.file_id === fileId
            ? { ...f, entity_type_names: entityTypeNames }
            : f
        )
      );
      setUploadedFiles(prev =>
        prev.map(f =>
          String(f.file_id) === fileIdStr
            ? { ...f, entity_type_name: primaryType }
            : f
        )
      );

      // Clear stale column mappings for this file — they reference properties
      // from the old entity type which won't exist on the new type.
      // handleUploadBeforeNext will re-suggest fresh mappings before the user
      // reaches the Mapping step.
      setColumnMappings(prev => {
        const { [fileIdStr]: _removed, ...rest } = prev;
        return rest;
      });

      if (entityTypeNames.length === 1) {
        // Single type — clear this file's multi-type state and mark dirty for re-suggestion
        clearMultiTypeModeForFile(fileId);
        setDirtyTypes(prev => new Set([...prev, fileId]));
      } else {
        // Multiple types — build/update per-file suggestion. Read the local
        // entry via `localFilesRef.current` (a ref mirror of `localFiles`)
        // so this callback doesn't depend on localFiles directly; otherwise
        // every upload-progress tick would change the callback's identity
        // and defeat memo on FileItemWrapper.
        const file = localFilesRef.current.find(f => f.file_id === fileId);
        const existingSuggestion =
          file?.multiTypeSuggestion ?? multiTypeSuggestions[fileId];

        const updatedEntityTypes = entityTypeNames.map(name => {
          const existing = existingSuggestion?.entity_types.find(
            et => et.entity_type_name === name
          );
          return (
            existing ?? {
              entity_type_name: name,
              mappings: {},
              new_properties: [],
            }
          );
        });

        handleMultiTypeSuggestionAccepted(fileId, {
          entity_types: updatedEntityTypes,
          associations: existingSuggestion?.associations ?? [],
          confidence: existingSuggestion?.confidence ?? 1.0,
          is_multi_type: true,
        });
      }
    },
    [
      setUploadedFiles,
      setColumnMappings,
      clearMultiTypeModeForFile,
      setDirtyTypes,
      multiTypeSuggestions,
      handleMultiTypeSuggestionAccepted,
    ]
  );

  // Cleanup: abort all in-flight requests and cancel pending rAFs on unmount
  useEffect(() => {
    const animationFrameIds = animationFrameIdsRef.current;
    return () => {
      abortControllersRef.current.forEach(controller => {
        controller.abort();
      });
      abortControllersRef.current = [];
      animationFrameIds.forEach(id => cancelAnimationFrame(id));
      animationFrameIds.clear();
    };
  }, []);

  // Cleanup: clear auto-activate message timer on unmount
  useEffect(() => {
    return () => {
      if (autoActivateTimerRef.current !== null) {
        clearTimeout(autoActivateTimerRef.current);
      }
    };
  }, []);

  return (
    <div className='space-y-6 py-4'>
      {/* Use role='status' aria-live='polite' for non-intrusive status announcements */}
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className='sr-only'
      >
        {autoActivateMessage}
      </div>
      {/* Import Name */}
      <div>
        <Input
          label='Import name'
          placeholder='e.g., Q4 Contacts'
          value={importName}
          onChange={setImportName}
          onBlur={handleImportNameBlur}
          size='md'
          maxLength={255}
          isInvalid={importName.trim().length === 0 && uploadedFiles.length > 0}
          showError={importNameTouched}
          hint={
            importNameTouched &&
            importName.trim().length === 0 &&
            uploadedFiles.length > 0
              ? 'Add a name to import this file'
              : undefined
          }
        />
      </div>

      {/* Dropzone */}
      <div>
        <label className='block text-sm font-medium text-fg-secondary mb-1.5'>
          CSV or Excel files
        </label>
        <FileDropZone
          onFilesSelected={handleFiles}
          acceptedTypes={CSV_ACCEPTED_TYPES}
          multiple
          hint='CSV or Excel files'
        />
      </div>

      {/* File List */}
      {localFiles.length > 0 && (
        <div className='space-y-3'>
          {localFiles.map(file => (
            <FileItemWrapper
              key={file.file_id || file.filename}
              file={file}
              onRemoveFile={handleRemove}
              onEntityTypeChange={handleEntityTypeChange}
              entityTypeOptions={entityTypeOptions}
            />
          ))}
        </div>
      )}

      {/* Validation message */}
      {localFiles.length === 0 && (
        <div className='rounded-lg bg-bg-secondary p-4 text-center text-sm text-fg-tertiary'>
          Upload at least one CSV or Excel file to continue
        </div>
      )}

      {/* AI data cleanup prompt */}
      <DataCleanupPrompt orgId={orgId} />
    </div>
  );
}

export default UploadStep;
